import { DateTime } from 'luxon';
import { List as ImmutableList } from 'immutable';
import type { RecordOf } from 'immutable';
import { useCallback, useMemo } from 'react';

import type { ChartOfAccount, ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import type { TagProps } from 'app-components/flux/tags/tagsTypes';
import { transactionsTypes } from 'app-components/flux/transactions';
import type {
  Attachment,
  CashFlowTransaction,
  QcsColor,
  SplitProps,
  TransactionProps,
} from 'app-components/flux/transactions/transactionsTypes';

import { transactionDetailsFormHooks } from './formHooks';

type TransactionDetailsFieldValues = {
  accountId: string;
  amount: number;
  attachments: Attachment[] | null | undefined;
  businessId: string | null | undefined;
  checkNumber: string;
  coaRaw: ChartOfAccountProps | null | undefined;
  customersAndProjects: ReturnType<typeof transactionDetailsFormHooks.useField<'customersAndProjects'>>['value'];
  excludeF2S: boolean | null | undefined;
  excludeReports: boolean | null | undefined;
  isBill: boolean;
  isReviewed: boolean;
  memo: string;
  originalTxnJS: TransactionProps;
  payee: string;
  postedOn: string;
  splitRaw: SplitProps | null;
  tagsArr: ReturnType<typeof transactionDetailsFormHooks.useField<'tags'>>['value'];
  userFlag: QcsColor | null | undefined;
};

type TransactionDetailsFieldSetters = {
  setAccountIdInternal: (value: string | null | undefined) => void;
  setAmount: (value: number) => void;
  setAttachments: (value: Attachment[]) => void;
  setBusinessId: (businessId: string | undefined) => void;
  setCheckNumberInternal: (value: string) => void;
  setCoa: (value: ChartOfAccount | ChartOfAccountProps | null | undefined) => void;
  setCoaInternal: (value: ChartOfAccountProps | null | undefined) => void;
  setCustomersAndProjectsInternal: (
    value: ReturnType<typeof transactionDetailsFormHooks.useField<'customersAndProjects'>>['value'],
  ) => void;
  setDate: (value: DateTime | null) => void;
  setExcludeF2S: (value: boolean) => void;
  setExcludeReports: (value: boolean) => void;
  setFlag: (value: QcsColor | null | undefined) => void;
  setIsBill: (value: boolean) => void;
  setIsBillInternal: (value: boolean) => void;
  setMemo: (value: string) => void;
  setPayee: (value: string) => void;
  setReviewedValue: (value: boolean) => void;
  setSplit: (value: RecordOf<SplitProps> | null) => void;
  setStatus: (value: 'CLEARED' | 'PENDING' | 'RECONCILED') => void;
  setTags: (value: TransactionProps['tags']) => void;
  updateAmount: (event: { target: { value: string } }) => void;
  updateReviewed: () => void;
};

type TransactionDetailsFieldDerived = {
  coa: ChartOfAccountProps | null | undefined;
  date: DateTime;
  isDirty: boolean;
  split: RecordOf<SplitProps> | null;
  status: 'CLEARED' | 'PENDING' | 'RECONCILED';
  txn: CashFlowTransaction;
};

type UseTransactionDetailsFieldsResult = {
  derived: TransactionDetailsFieldDerived;
  setters: TransactionDetailsFieldSetters;
  values: TransactionDetailsFieldValues;
};

export const normalizeTagsForTransactionDetailsForm = (value: TransactionProps['tags']): TagProps[] => {
  const tags = value ? (ImmutableList.isList(value) ? value.toArray() : value) : [];
  return tags.map((tag) => ('toObject' in tag ? tag.toObject() : tag));
};

export function useTransactionDetailsFormFields(isPrefilled: boolean | undefined): UseTransactionDetailsFieldsResult {
  const { value: userFlag, onValueChange: setFlag } = transactionDetailsFormHooks.useField('userFlag');
  const { value: isReviewed, onValueChange: setReviewedValue } = transactionDetailsFormHooks.useField('isReviewed');
  const { value: accountId, onValueChange: setAccountIdInternal } = transactionDetailsFormHooks.useField('accountId');
  const { value: txnState, onValueChange: setStatusInternal } = transactionDetailsFormHooks.useField('txnState');
  const { value: postedOn, onValueChange: setPostedOn } = transactionDetailsFormHooks.useField('postedOn');
  const { value: payee, onValueChange: setPayeeInternal } = transactionDetailsFormHooks.useField('payee');
  const { value: amount, onValueChange: setAmountInternal } = transactionDetailsFormHooks.useField('amount');
  const { value: coaRaw, onValueChange: setCoaInternal } = transactionDetailsFormHooks.useField('coa');
  const { value: checkNumber, onValueChange: setCheckNumberInternal } =
    transactionDetailsFormHooks.useField('checkNumber');
  const { value: tagsArr, onValueChange: setTagsArr } = transactionDetailsFormHooks.useField('tags');
  const { value: excludeReports, onValueChange: setExcludeReportsInternal } =
    transactionDetailsFormHooks.useField('isExcludedFromReports');
  const { value: excludeF2S, onValueChange: setExcludeF2SInternal } =
    transactionDetailsFormHooks.useField('isExcludedFromF2S');
  const { value: memo, onValueChange: setMemoInternal } = transactionDetailsFormHooks.useField('memo');
  const { value: attachments, onValueChange: setAttachmentsInternal } =
    transactionDetailsFormHooks.useField('attachments');
  const { value: splitRaw, onValueChange: setSplitInternal } = transactionDetailsFormHooks.useField('split');
  const { value: isBill, onValueChange: setIsBillInternal } = transactionDetailsFormHooks.useField('isBill');
  const { value: businessId, onValueChange: setBusinessIdInternal } =
    transactionDetailsFormHooks.useField('businessId');
  const { value: customersAndProjects, onValueChange: setCustomersAndProjectsInternal } =
    transactionDetailsFormHooks.useField('customersAndProjects');

  const formIsDirty = transactionDetailsFormHooks.useIsDirty();
  const isDirty = formIsDirty || isPrefilled || false;
  const originalTxnJS = transactionDetailsFormHooks.useSelector(({ state }) => state.originalTxnJS);
  const txn = useMemo(() => transactionsTypes.mkCashFlowTransaction(originalTxnJS), [originalTxnJS]);

  const coa = coaRaw;
  const status = txnState;
  const date = useMemo(() => DateTime.fromISO(postedOn), [postedOn]);
  const split = useMemo(() => (splitRaw ? transactionsTypes.mkSplit(splitRaw) : null), [splitRaw]);

  const setTags = useCallback(
    (value: TransactionProps['tags']) => {
      setTagsArr(normalizeTagsForTransactionDetailsForm(value));
    },
    [setTagsArr],
  );
  const setSplit = useCallback(
    (value: RecordOf<SplitProps> | null) => {
      setSplitInternal(value ? { doPatch: value.doPatch, items: value.items } : null);
    },
    [setSplitInternal],
  );
  const setExcludeReports = useCallback(
    (value: boolean) => setExcludeReportsInternal(value),
    [setExcludeReportsInternal],
  );
  const setExcludeF2S = useCallback((value: boolean) => setExcludeF2SInternal(value), [setExcludeF2SInternal]);
  const setCoa = useCallback(
    (value: ChartOfAccount | ChartOfAccountProps | null | undefined) => {
      setCoaInternal(value && 'toObject' in value ? value.toObject() : value);
    },
    [setCoaInternal],
  );
  const setBusinessId = useCallback(
    (businessIdParam: string | undefined) => {
      setBusinessIdInternal(businessIdParam);
    },
    [setBusinessIdInternal],
  );
  const updateAmount = useCallback(
    (event: { target: { value: string } }) => {
      setAmountInternal(Number(event.target.value));
    },
    [setAmountInternal],
  );
  const updateReviewed = useCallback(() => {
    setReviewedValue(!isReviewed);
  }, [isReviewed, setReviewedValue]);
  const setAmount = useCallback((value: number) => setAmountInternal(value), [setAmountInternal]);
  const setDate = useCallback(
    (value: DateTime | null) => {
      setPostedOn(value?.isValid ? value.toISODate() || '' : '');
    },
    [setPostedOn],
  );
  const setPayee = useCallback((value: string) => setPayeeInternal(value), [setPayeeInternal]);
  const setMemo = useCallback((value: string) => setMemoInternal(value), [setMemoInternal]);
  const setAttachments = useCallback((value: Attachment[]) => setAttachmentsInternal(value), [setAttachmentsInternal]);
  const setStatus = useCallback(
    (value: 'CLEARED' | 'PENDING' | 'RECONCILED') => setStatusInternal(value),
    [setStatusInternal],
  );
  const setIsBill = useCallback((value: boolean) => setIsBillInternal(value), [setIsBillInternal]);

  return {
    derived: {
      coa,
      date,
      isDirty,
      split,
      status,
      txn,
    },
    setters: {
      setAccountIdInternal,
      setAmount,
      setAttachments,
      setBusinessId,
      setCheckNumberInternal,
      setCoa,
      setCoaInternal,
      setCustomersAndProjectsInternal,
      setDate,
      setExcludeF2S,
      setExcludeReports,
      setFlag,
      setIsBill,
      setIsBillInternal,
      setMemo,
      setPayee,
      setReviewedValue,
      setSplit,
      setStatus,
      setTags,
      updateAmount,
      updateReviewed,
    },
    values: {
      accountId,
      amount,
      attachments,
      businessId,
      checkNumber,
      coaRaw,
      customersAndProjects,
      excludeF2S,
      excludeReports,
      isBill,
      isReviewed,
      memo,
      originalTxnJS,
      payee,
      postedOn,
      splitRaw,
      tagsArr,
      userFlag,
    },
  };
}
